import * as React from 'react';
import {
  withSystem,
  EnvTypes,
  Plugins,
  TypeImplementation,
} from '@packages/systems/wfdl';
import {
  TList,
  functionOf,
  TTypeConstructor,
  EList,
  ELiteral,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  TPredicate,
  toBooleanTag,
} from '@packages/systems/plugins-utils/Boolean';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {
  ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {Autocomplete} from '@packages/systems/spring';
import {createUseSelector} from '@packages/systems/flux';
import type {LocalizationState} from '@packages/systems/localization-types';
import {
  LocalesTagType,
  LocalesLanguageCodeType,
  LocalesCountryCodeType,
} from './types';

const useLocalizationSlice = createUseSelector<{
  LocalizationStore: LocalizationState;
}>();

const selectLocalizationState = (state: {
  LocalizationStore: LocalizationState;
}) => state.LocalizationStore;

const useLocalizationSelector = <T,>(
  selector: (state: LocalizationState) => T
) => {
  const state = useLocalizationSlice(selectLocalizationState);
  return selector(state);
};

function getValuesFromExpression(expr: ContextualExpression): string[] {
  const listExpr = ContextualExpressions.getList(stripAnn(expr));
  return listExpr
    ? listExpr.reduce<string[]>((acc, item) => {
        const value = ContextualExpressions.getLiteral(stripAnn(item))?.value;
        if (typeof value === 'string') {
          acc.push(value);
        }
        return acc;
      }, [])
    : [];
}

function toOptions(values: Set<string>): Array<{value: string; label: string}> {
  return Array.from(values).map((value) => ({
    value,
    label: value,
  }));
}

function RenderTagAnyOfList({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) {
  const options = useLocalizationSelector((state) => {
    const tagsSet = new Set<string>();
    if (state.primaryLocale?.tag) {
      tagsSet.add(state.primaryLocale.tag);
    }
    state.locales.forEach((locale) => {
      if (locale.tag) {
        tagsSet.add(locale.tag);
      }
    });
    return toOptions(tagsSet);
  });

  const currentValues = getValuesFromExpression(expr);

  const listDispatch = React.useCallback(
    (values: string[]) => {
      const list = values.map((value) =>
        ELiteral({
          name: qualify(Plugins.name('Localization'), typeName('Tag')),
          value,
        })
      );
      dispatch(updateExpressionAction(EList(list)));
    },
    [dispatch]
  );

  return (
    <Autocomplete
      aria-label="Select locale tags"
      value={currentValues}
      selectionMode="multiple"
      options={options}
      onChange={listDispatch}
    />
  );
}

function RenderLanguageCodeAnyOfList({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) {
  const options = useLocalizationSelector((state) => {
    const codesSet = new Set<string>();
    if (state.primaryLocale?.tag) {
      // Extract language code from locale tag (e.g., "en" from "en-US" or just "en")
      const [languageCode] = state.primaryLocale.tag.split('-');
      if (languageCode) {
        codesSet.add(languageCode);
      }
    }
    state.locales.forEach((locale) => {
      if (locale.tag) {
        // Extract language code from locale tag (e.g., "en" from "en-US" or just "en")
        const [languageCode] = locale.tag.split('-');
        if (languageCode) {
          codesSet.add(languageCode);
        }
      }
    });
    return toOptions(codesSet);
  });

  const currentValues = getValuesFromExpression(expr);

  const listDispatch = React.useCallback(
    (values: string[]) => {
      const list = values.map((value) =>
        ELiteral({
          name: qualify(Plugins.name('Localization'), typeName('LanguageCode')),
          value,
        })
      );
      dispatch(updateExpressionAction(EList(list)));
    },
    [dispatch]
  );

  return (
    <Autocomplete
      aria-label="Select language codes"
      value={currentValues}
      selectionMode="multiple"
      options={options}
      onChange={listDispatch}
    />
  );
}

function RenderCountryCodeAnyOfList({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) {
  const options = useLocalizationSelector((state) => {
    const codesSet = new Set<string>();
    if (state.primaryLocale?.tag) {
      // Only extract country code if locale has both language and country parts
      const parts = state.primaryLocale.tag.split('-');
      const countryCode = parts.length > 1 ? parts[1] : undefined;
      if (countryCode) {
        codesSet.add(countryCode);
      }
    }
    state.locales.forEach((locale) => {
      if (locale.tag) {
        // Only extract country code if locale has both language and country parts
        const parts = locale.tag.split('-');
        const countryCode = parts.length > 1 ? parts[1] : undefined;
        if (countryCode) {
          codesSet.add(countryCode);
        }
      }
    });
    return toOptions(codesSet);
  });

  const currentValues = getValuesFromExpression(expr);

  const listDispatch = React.useCallback(
    (values: string[]) => {
      const list = values.map((value) =>
        ELiteral({
          name: qualify(Plugins.name('Localization'), typeName('CountryCode')),
          value,
        })
      );
      dispatch(updateExpressionAction(EList(list)));
    },
    [dispatch]
  );

  return (
    <Autocomplete
      aria-label="Select country codes"
      value={currentValues}
      selectionMode="multiple"
      options={options}
      onChange={listDispatch}
    />
  );
}

export const TagAnyOfList = TypeImplementation.Alias({
  type: TList(LocalesTagType),
  render: RenderTagAnyOfList,
});

export const LanguageCodeAnyOfList = TypeImplementation.Alias({
  type: TList(LocalesLanguageCodeType),
  render: RenderLanguageCodeAnyOfList,
});

export const CountryCodeAnyOfList = TypeImplementation.Alias({
  type: TList(LocalesCountryCodeType),
  render: RenderCountryCodeAnyOfList,
});

export const tagIsAny: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is any'},
      functionOf(
        withSystem(
          {isBindable: true},
          TTypeConstructor(
            qualify(Plugins.name('Localization'), typeName('TagAnyOfList'))
          )
        ),
        TPredicate(LocalesTagType)
      )
    ),
    exported: 'visible',
  },
  value: (predArgList: string[]) => (fieldVal: string) => {
    return toBooleanTag(predArgList.includes(fieldVal));
  },
};

export const tagIsNotAny: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is not any'},
      functionOf(
        withSystem(
          {isBindable: true},
          TTypeConstructor(
            qualify(Plugins.name('Localization'), typeName('TagAnyOfList'))
          )
        ),
        TPredicate(LocalesTagType)
      )
    ),
    exported: 'visible',
  },
  value: (predArgList: string[]) => (fieldVal: string) => {
    return toBooleanTag(!predArgList.includes(fieldVal));
  },
};

export const languageCodeIsAny: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is any'},
      functionOf(
        withSystem(
          {isBindable: true},
          TTypeConstructor(
            qualify(
              Plugins.name('Localization'),
              typeName('LanguageCodeAnyOfList')
            )
          )
        ),
        TPredicate(LocalesLanguageCodeType)
      )
    ),
    exported: 'visible',
  },
  value: (predArgList: string[]) => (fieldVal: string) => {
    return toBooleanTag(predArgList.includes(fieldVal));
  },
};

export const languageCodeIsNotAny: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is not any'},
      functionOf(
        withSystem(
          {isBindable: true},
          TTypeConstructor(
            qualify(
              Plugins.name('Localization'),
              typeName('LanguageCodeAnyOfList')
            )
          )
        ),
        TPredicate(LocalesLanguageCodeType)
      )
    ),
    exported: 'visible',
  },
  value: (predArgList: string[]) => (fieldVal: string) => {
    return toBooleanTag(!predArgList.includes(fieldVal));
  },
};

export const countryCodeIsAny: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is any'},
      functionOf(
        withSystem(
          {isBindable: true},
          TTypeConstructor(
            qualify(
              Plugins.name('Localization'),
              typeName('CountryCodeAnyOfList')
            )
          )
        ),
        TPredicate(LocalesCountryCodeType)
      )
    ),
    exported: 'visible',
  },
  value: (predArgList: string[]) => (fieldVal: string) => {
    return toBooleanTag(predArgList.includes(fieldVal));
  },
};

export const countryCodeIsNotAny: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is not any'},
      functionOf(
        withSystem(
          {isBindable: true},
          TTypeConstructor(
            qualify(
              Plugins.name('Localization'),
              typeName('CountryCodeAnyOfList')
            )
          )
        ),
        TPredicate(LocalesCountryCodeType)
      )
    ),
    exported: 'visible',
  },
  value: (predArgList: string[]) => (fieldVal: string) => {
    return toBooleanTag(!predArgList.includes(fieldVal));
  },
};

function isCountryCodeValueSet(countryCode: unknown) {
  return typeof countryCode === 'string' && countryCode !== '';
}

export const countryCodeIsSet: EnvTypes.UnsafeTypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem({label: 'is set'}, TPredicate(LocalesCountryCodeType)),
  },
  value: (countryCode: unknown) => {
    return toBooleanTag(isCountryCodeValueSet(countryCode));
  },
};

export const countryCodeIsNotSet: EnvTypes.UnsafeTypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem(
      {label: 'is not set'},
      TPredicate(LocalesCountryCodeType)
    ),
  },
  value: (countryCode: unknown) => {
    return toBooleanTag(!isCountryCodeValueSet(countryCode));
  },
};
